import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { SUPPORTED_LANGUAGES, type SupportedLanguage } from "@/lib/i18n";
import { cn } from "@/lib/utils";

// The storefront ships PL-only for now; this switcher is the forward-looking
// opt-in (Leon): PL is always the default a visitor lands on (see `lib/i18n`),
// and this is the subtle-but-visible way to change it. Picking a language calls
// `i18n.changeLanguage`, which persists the choice to localStorage (i18n
// `detection.caches`) so it survives reloads and navigation for that browser.
//
// Endonyms (Polski / English / Deutsch) label each option regardless of the
// current UI language — the switcher convention — while the visible control is
// the compact language code. Rendered in the footer, styled to sit quietly in
// the muted footer text with the active language the only one at full contrast.

const ENDONYMS: Record<SupportedLanguage, string> = {
  pl: "Polski",
  en: "English",
  de: "Deutsch",
};

export function LanguageSwitcher({ className }: { className?: string }) {
  const { i18n, t } = useTranslation();
  const active = (i18n.resolvedLanguage ?? "pl") as SupportedLanguage;

  return (
    // A <fieldset> (implicit `group` role) carries the accessible group name
    // without the non-semantic `role="group"`; reset to sit inline in the footer.
    <fieldset
      aria-label={t("language.label")}
      className={cn("m-0 flex min-w-0 items-center gap-1.5 border-0 p-0 text-xs", className)}
    >
      {SUPPORTED_LANGUAGES.map((lng, index) => (
        <Fragment key={lng}>
          {index > 0 ? (
            <span aria-hidden="true" className="text-border">
              ·
            </span>
          ) : null}
          <button
            type="button"
            lang={lng}
            aria-label={ENDONYMS[lng]}
            aria-current={lng === active}
            onClick={() => {
              if (lng !== active) void i18n.changeLanguage(lng);
            }}
            className={cn(
              "rounded uppercase tracking-wide transition-colors focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring",
              lng === active
                ? "font-medium text-foreground"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {lng}
          </button>
        </Fragment>
      ))}
    </fieldset>
  );
}
